import React from 'react';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { OverlayComponent } from 'app/components';
import { hasTranslationsValid } from 'app/components/translation/has-translations-valid';

import { getTranslationForLanguage, normalizeTextItem, validateTextItem } from '../utils';
import { TextConfigurationFormState, TextConfigurationItem, TextItemValidation } from '../types-store';
import { TextConfigurationTranslation } from '../../constants';

import { TextFormStickyHeader } from './text-form-sticky-header';

interface TextFormContainerProps {
  formState: TextConfigurationFormState;
  mainLanguage: number;
  development: boolean;

  switchTranslationsToggle: (value: boolean) => void;
  deleteText: (textId: number, textSet: number, successMessage: string) => void;
  saveText: (text: TextConfigurationItem,
    successMessage: string,
    validationErrorMessage: string) => void;
  updateValidation: (validation: TextItemValidation) => void;
  displayError: (errorMessage: string) => void;

  validation: TextItemValidation;
  translation: TextConfigurationTranslation;
  children?: React.ReactNode;
}

export const TextFormContainer: React.FC<TextFormContainerProps> = ({
  formState, mainLanguage, development,
  switchTranslationsToggle,
  deleteText, saveText, updateValidation, displayError,
  translation,
  children
}) => {

  const { textItem, saving, deleting, languageOptions, validation } = formState;

  const onSave = () => {
    const formValidation = validateTextItem(textItem, languageOptions, translation.must_be_filled_out);

    updateValidation({ ...validation, ...formValidation });

    if (Object.keys(formValidation).length > 0) {
      !hasTranslationsValid(formValidation.language, mainLanguage) && switchTranslationsToggle(true);
      displayError(translation.text_validation_error);
      return;
    }

    saveText(normalizeTextItem(textItem),
      isDefinedIncludingZero(textItem && textItem.id)
        ? translation.text_updated
        : translation.text_created,
      translation.text_validation_error);
  };

  const onDelete = () => {
    isDefinedIncludingZero(textItem && textItem.id) &&
      isDefinedIncludingZero(textItem && textItem.textSet && textItem.textSet.value) &&
      deleteText(textItem && textItem.id,
        textItem && textItem.textSet && textItem.textSet.value,
        translation.text_deleted);
  };

  const mainLanguageTranslation = getTranslationForLanguage(textItem, mainLanguage);

  return <>
    <OverlayComponent
      displayOverlay={saving || deleting}
      overlayText={saving
        ? translation.saving_text
        : translation.deleting_text} />
    <TextFormStickyHeader
      textItem={textItem}
      language={mainLanguageTranslation}
      development={development}
      disabled={formState.fetching}
      onSave={onSave}
      onDelete={onDelete}
      translation={translation} />
    {children}
  </>;
};
